import { replaceAll } from "src/pages/TransformationPage/views/TransformationCodeTabContent/TransformationCodeTabContent.utils";
import { makeQuery } from "src/queries/makeQuery";
import type { TaskRunEventResponse } from "../tasks/tasks.types";
import type { PhysicalPlanResponse, TransformationResponse } from "./tfs.types";

const defaultSelect = (data: TransformationResponse[]) =>
  data.map((d) => ({
    ...d,
    query_str: replaceAll(d.query_str || "", d.query_vars),
    hasChange: d.query_str && d.prev_query && d.query_str !== d.prev_query,
  }));

export const useQueryTaskTfs = makeQuery<
  TransformationResponse[],
  TransformationResponse[]
>((p) => `/api/tasks/${p.task_id}/tfs`, { select: defaultSelect });

/** Single transformation by tf_id */
export const useQueryTransformation = makeQuery<TransformationResponse>(
  (p) => `/api/tfs/${p.tf_id}`
);

/** /api/tfs/{tf_id}/events?sub_category=physical_plan */
export const useQueryTfPhysicalPlan = makeQuery<PhysicalPlanResponse[]>(
  (p) => `/api/tfs/${p.tf_id}/events`,
  { queryParams: [{ sub_category: () => "physical_plan" }] }
);

/** /api/tfs/{tf_id}/stages — stage events belonging to physical plans of this transformation */
export const useQueryTfStages = makeQuery<TaskRunEventResponse[]>(
  (p) => `/api/tfs/${p.tf_id}/stages`
);

/**
 * Compacts a physical plan tree by grouping consecutive nodes with the same stages
 * into "stage" nodes. This converts sequences like 1-1-1-2-2-3 into stage(1)-stage(2)-stage(3),
 * where each stage node contains the subtree of all nodes from that stage.
 */
export const compactPhysicalPlan = (
  response: PhysicalPlanResponse
): PhysicalPlanResponse => {
  type PlanNode = PhysicalPlanResponse["payload"]["plan"];

  const areSameStages = (stages1: number[], stages2: number[]): boolean => {
    return (
      stages1.length > 0 &&
      stages1.length === stages2.length &&
      stages1.every((stage) => stages2.includes(stage)) &&
      stages2.every((stage) => stages1.includes(stage))
    );
  };

  // Recursively collect all descendant nodes that have the same stages
  const collectSameStageDescendants = (
    node: PlanNode,
    targetStages: number[]
  ): { collected: PlanNode[]; nextChildren: PlanNode[] } => {
    const collected: PlanNode[] = [node];
    const nextChildren: PlanNode[] = [];

    if (!node.children || node.children.length === 0) {
      return { collected, nextChildren };
    }

    for (const child of node.children) {
      const childStages = child.stages || [];
      if (areSameStages(targetStages, childStages)) {
        // Child has same stages - collect it and its descendants
        const result = collectSameStageDescendants(child, targetStages);
        collected.push(...result.collected);
        nextChildren.push(...result.nextChildren);
      } else {
        // Child has different stages - this is a next child
        nextChildren.push(child);
      }
    }

    return { collected, nextChildren };
  };

  const compactNode = (node: PlanNode, depth = 0): PlanNode => {
    // Prevent infinite recursion
    if (depth > 100) {
      console.error("Max recursion depth reached in compactPhysicalPlan");
      return node;
    }

    if (!node.children || node.children.length === 0) {
      return node;
    }

    const nodeStages = node.stages || [];
    const newChildren: PlanNode[] = [];

    for (const child of node.children) {
      const childStages = child.stages || [];

      // Check if child has same stages as parent
      if (areSameStages(nodeStages, childStages)) {
        // Collect this child and all its same-stage descendants
        const { collected, nextChildren } = collectSameStageDescendants(
          child,
          nodeStages
        );

        // Check if the last collected node is a leaf (has no children and no nextChildren)
        const lastCollected = collected[collected.length - 1];
        const isLastALeaf =
          nextChildren.length === 0 &&
          (!lastCollected.children || lastCollected.children.length === 0);

        if (isLastALeaf && collected.length > 1) {
          // Remove the last leaf from collected and keep it as a separate child
          const leafNode = collected.pop()!;

          // Create a stage node with remaining nodes (if any)
          const stageNode: PlanNode = {
            name: `Stage ${childStages.join(", ")}`,
            description: `Grouped ${collected.length} node(s) from stage(s) ${childStages.join(", ")}`,
            stages: childStages,
            isStageNode: true,
            subtrees: collected,
            children: [leafNode],
          };

          newChildren.push(stageNode);
        } else {
          // Create a stage node to contain all same-stage nodes
          const stageNode: PlanNode = {
            name: `Stage ${childStages.join(", ")}`,
            description: `Grouped ${collected.length} node(s) from stage(s) ${childStages.join(", ")}`,
            stages: childStages,
            isStageNode: true,
            subtrees: collected,
            // Recursively compact the next children (which have different stages)
            children:
              nextChildren.length > 0
                ? nextChildren.map((c) => compactNode(c, depth + 1))
                : undefined,
          };

          newChildren.push(stageNode);
        }
      } else {
        // Different stages - recursively compact this child
        newChildren.push(compactNode(child, depth + 1));
      }
    }

    return {
      ...node,
      children: newChildren.length > 0 ? newChildren : undefined,
    };
  };

  return {
    ...response,
    payload: {
      ...response.payload,
      plan: compactNode(response.payload.plan, 0),
    },
  };
};
